/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { PrimaryButton } from "../../components/buttons/PrimaryButton";

dayjs.extend(isoWeek);

/** Last 12 months: 12 months ago -> current month */
function getLast12MonthsRolling() {
  const end = dayjs(); // current month
  const start = end.subtract(12, "month");
  return {
    fromYear: start.year(),
    fromMonth: start.month() + 1,
    toYear: end.year(),
    toMonth: end.month() + 1,
    fromWeek: start.isoWeek(),
    toWeek: end.isoWeek(),
    fromQuarter: Math.ceil((start.month() + 1) / 3),
    toQuarter: Math.ceil((end.month() + 1) / 3),
  };
}

export interface MaterialsChartFiltersState {
  period: "week" | "month" | "quarter" | "year";
  fromYear: number;
  toYear: number;
  fromQuarter: number;
  toQuarter: number;
  fromMonth: number;
  toMonth: number;
  fromWeek: number;
  toWeek: number;
}

const defaultState: MaterialsChartFiltersState = (() => {
  const rolling = getLast12MonthsRolling();
  return {
    period: "month",
    fromYear: rolling.fromYear,
    toYear: rolling.toYear,
    fromQuarter: rolling.fromQuarter,
    toQuarter: rolling.toQuarter,
    fromMonth: rolling.fromMonth,
    toMonth: rolling.toMonth,
    fromWeek: rolling.fromWeek,
    toWeek: rolling.toWeek,
  };
})();

interface MaterialsChartFiltersProps {
  state: MaterialsChartFiltersState;
  onChange: (state: MaterialsChartFiltersState) => void;
  onDownloadImage?: () => void;
  onDownloadCsv?: () => void;
  isCsvDownloading?: boolean;
  hasData?: boolean;
  /** When false, hide 'Week' from period selector (used for parcel charts) */
  allowWeekly?: boolean;
}

export const getDefaultMaterialsChartFilters = () => ({ ...defaultState });

export function MaterialsChartFilters({
  state,
  onChange,
  onDownloadImage,
  onDownloadCsv,
  isCsvDownloading = false,
  hasData = true,
  allowWeekly = true,
}: MaterialsChartFiltersProps) {
  const {
    period,
    fromYear,
    toYear,
    fromQuarter,
    toQuarter,
    fromMonth,
    toMonth,
    fromWeek,
    toWeek,
  } = state;

  const currentYear = dayjs().year();
  const currentMonth = dayjs().month() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);
  const currentWeek = dayjs().isoWeek();

  const yearOptions = Array.from(
    { length: currentYear - 2023 },
    (_, i) => currentYear - i
  );

  const getQuarterOptions = (year: number) =>
    [1, 2, 3, 4].map((q) => ({
      value: q,
      label: `Q${q}`,
      disabled: year === currentYear && q > currentQuarter,
    }));

  const getMonthOptions = (year: number) =>
    Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: dayjs().month(i).format("MMM"),
      disabled: year === currentYear && i + 1 > currentMonth,
    }));

  const getWeekOptions = (year: number) => {
    const lastDayOfYear = dayjs(`${year}-12-31`);
    const totalWeeks =
      lastDayOfYear.isoWeek() === 1 ? 52 : lastDayOfYear.isoWeek();
    return Array.from({ length: totalWeeks }, (_, i) => {
      const weekNumber = i + 1;
      const startOfWeek = dayjs()
        .year(year)
        .isoWeek(weekNumber)
        .startOf("isoWeek");
      const endOfWeek = dayjs().year(year).isoWeek(weekNumber).endOf("isoWeek");
      return {
        value: weekNumber,
        label: `Week ${weekNumber}: ${startOfWeek.format("MMM DD")} - ${endOfWeek.format("MMM DD")}`,
        disabled: year === currentYear && weekNumber > currentWeek,
      };
    });
  };

  const fromWeekOptions = useMemo(() => getWeekOptions(fromYear), [fromYear]);
  const toWeekOptions = useMemo(() => getWeekOptions(toYear), [toYear]);

  const setPeriod = (p: MaterialsChartFiltersState["period"]) =>
    onChange({ ...state, period: p });
  const setFromYear = (v: number) => onChange({ ...state, fromYear: v });
  const setToYear = (v: number) => onChange({ ...state, toYear: v });
  const setFromQuarter = (v: number) => onChange({ ...state, fromQuarter: v });
  const setToQuarter = (v: number) => onChange({ ...state, toQuarter: v });
  const setFromMonth = (v: number) => onChange({ ...state, fromMonth: v });
  const setToMonth = (v: number) => onChange({ ...state, toMonth: v });
  const setFromWeek = (v: number) => onChange({ ...state, fromWeek: v });
  const setToWeek = (v: number) => onChange({ ...state, toWeek: v });

  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-start sm:justify-end gap-2 sm:gap-3">
      <Menu>
        <MenuHandler>
          <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
            {period.charAt(0).toUpperCase() + period.slice(1)}
            <ChevronDown className="ml-1 sm:ml-2" />
          </button>
        </MenuHandler>
        <MenuList placeholder={undefined}>
          {(allowWeekly
            ? (["year", "quarter", "month", "week"] as const)
            : (["year", "quarter", "month"] as const)
          ).map((p) => (
            <MenuItem
              key={p}
              disabled={period === p}
              onClick={() => setPeriod(p)}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      {period === "year" && (
        <>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {fromYear}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  disabled={fromYear === y || y > currentYear}
                  onClick={() => setFromYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <span className="text-gray-text text-xs sm:text-sm whitespace-nowrap">
            to
          </span>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {toYear}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  disabled={toYear === y || y > currentYear}
                  onClick={() => setToYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </>
      )}

      {period === "quarter" && (
        <>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {fromYear}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  disabled={fromYear === y || y > currentYear}
                  onClick={() => setFromYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {getQuarterOptions(fromYear).find(
                  (q) => q.value === fromQuarter
                )?.label || "Q1"}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {getQuarterOptions(fromYear).map((q) => (
                <MenuItem
                  key={q.value}
                  disabled={q.disabled || fromQuarter === q.value}
                  onClick={() => setFromQuarter(q.value)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {q.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <span className="text-gray-text text-xs sm:text-sm whitespace-nowrap">
            to
          </span>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {toYear}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  disabled={toYear === y || y > currentYear}
                  onClick={() => setToYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {getQuarterOptions(toYear).find((q) => q.value === toQuarter)
                  ?.label || "Q4"}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {getQuarterOptions(toYear).map((q) => (
                <MenuItem
                  key={q.value}
                  disabled={q.disabled || toQuarter === q.value}
                  onClick={() => setToQuarter(q.value)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {q.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </>
      )}

      {period === "month" && (
        <>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {fromYear}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  disabled={fromYear === y || y > currentYear}
                  onClick={() => setFromYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {getMonthOptions(fromYear).find((m) => m.value === fromMonth)
                  ?.label || "Jan"}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {getMonthOptions(fromYear).map((m) => (
                <MenuItem
                  key={m.value}
                  disabled={m.disabled || fromMonth === m.value}
                  onClick={() => setFromMonth(m.value)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {m.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <span className="text-gray-text text-xs sm:text-sm whitespace-nowrap">
            to
          </span>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {toYear}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  disabled={toYear === y || y > currentYear}
                  onClick={() => setToYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {getMonthOptions(toYear).find((m) => m.value === toMonth)
                  ?.label || "Jan"}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {getMonthOptions(toYear).map((m) => (
                <MenuItem
                  key={m.value}
                  disabled={m.disabled || toMonth === m.value}
                  onClick={() => setToMonth(m.value)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {m.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </>
      )}

      {period === "week" && (
        <>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {fromYear}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  disabled={fromYear === y || y > currentYear}
                  onClick={() => setFromYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Menu>
            <MenuHandler>
              <button
                className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                title={fromWeekOptions.find((w) => w.value === fromWeek)?.label}
              >
                {fromWeekOptions.find((w) => w.value === fromWeek)?.label ||
                  "Week"}
                <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
              </button>
            </MenuHandler>
            <MenuList
              className="rounded-none max-h-[250px] overflow-y-auto"
              placeholder={undefined}
            >
              {fromWeekOptions.map((w) => (
                <MenuItem
                  key={w.value}
                  disabled={w.disabled || fromWeek === w.value}
                  onClick={() => setFromWeek(w.value)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {w.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <span className="text-gray-text text-xs sm:text-sm whitespace-nowrap">
            to
          </span>
          <Menu>
            <MenuHandler>
              <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                {toYear}
                <ChevronDown className="ml-1 sm:ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  disabled={toYear === y || y > currentYear}
                  onClick={() => setToYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Menu>
            <MenuHandler>
              <button
                className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                title={toWeekOptions.find((w) => w.value === toWeek)?.label}
              >
                {toWeekOptions.find((w) => w.value === toWeek)?.label || "Week"}
                <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
              </button>
            </MenuHandler>
            <MenuList
              className="rounded-none max-h-[250px] overflow-y-auto"
              placeholder={undefined}
            >
              {toWeekOptions.map((w) => (
                <MenuItem
                  key={w.value}
                  disabled={w.disabled || toWeek === w.value}
                  onClick={() => setToWeek(w.value)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {w.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </>
      )}

      {hasData && onDownloadCsv && (
        <button
          onClick={onDownloadCsv}
          disabled={isCsvDownloading}
          className={`bg-droppie-oranje border border-gray-border h-8 px-3 sm:px-6 py-0 rounded active:bg-droppie-zalm disabled:bg-sky-light transition ease-linear flex items-center justify-center gap-2 ${
            isCsvDownloading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {isCsvDownloading ? (
            <>
              <svg
                aria-hidden="true"
                className="w-4 h-4 text-gray-200 animate-spin fill-white"
                viewBox="0 0 100 101"
                fill="none"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <p className="font-satoshi font-medium text-white text-xs sm:text-sm">
                Downloading...
              </p>
            </>
          ) : (
            <p className="font-satoshi font-medium text-white text-md sm:text-md">
              Download CSV
            </p>
          )}
        </button>
      )}
      {hasData && onDownloadImage && (
        <PrimaryButton
          size="sm"
          onClick={onDownloadImage}
          label="Download image"
          className="!bg-droppie-oranje !border-droppie-oranje"
        />
      )}
    </div>
  );
}
